'use client';

import { useRef, type ReactNode } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useScrollShadow } from '@benzinga/hooks';

export type DashboardPillTabDensity = 'default' | 'compact' | 'dense';

export const dashboardPillTabClassName = (
  isActive: boolean,
  density: DashboardPillTabDensity = 'default',
): string => {
  const sizeClassName =
    density === 'dense'
      ? 'px-2 py-0.5 text-[11px] font-bold'
      : density === 'compact'
        ? 'px-2.5 py-1 text-xs font-bold'
        : 'px-4 py-1.5 text-[15px] font-bold';
  return `shrink-0 rounded-md ${sizeClassName} ${
    isActive ? 'bg-[#3F83F8] text-[#F2F8FF]' : 'bg-[#3F83F81A] text-[#3F83F8] hover:bg-[#3F83F82A]'
  }`;
};

const fadeButtonClassName =
  'absolute inset-y-0 z-10 flex w-7 items-center text-[#3F83F8]';

export const DashboardPillTabs = <T extends string>({
  activeTab,
  density = 'default',
  onChange,
  onPrefetch,
  tabs,
}: {
  activeTab: T;
  density?: DashboardPillTabDensity;
  onChange?: (tab: T) => void;
  onPrefetch?: (tab: T) => void;
  tabs: { key: T; label?: ReactNode; name: string }[];
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { showEnd, showStart } = useScrollShadow(scrollRef);

  const scrollByTab = (direction: -1 | 1) => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const firstTab = element.firstElementChild;
    const step = firstTab instanceof HTMLElement ? firstTab.offsetWidth + 6 : 80;
    element.scrollBy({ behavior: 'smooth', left: direction * step });
  };

  return (
    <div className="relative min-w-0 w-full flex-1 overflow-hidden">
      <div
        className="flex min-w-0 gap-1.5 overflow-x-auto pr-7 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-pill-tabs=""
        ref={scrollRef}
      >
        {tabs.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button
              aria-label={tab.label ? tab.name : undefined}
              aria-pressed={isActive}
              className={dashboardPillTabClassName(isActive, density)}
              key={tab.key}
              onClick={onChange ? () => onChange(tab.key) : undefined}
              onFocus={onPrefetch ? () => onPrefetch(tab.key) : undefined}
              onPointerEnter={
                onPrefetch
                  ? event => {
                      if (event.pointerType === 'mouse') {
                        onPrefetch(tab.key);
                      }
                    }
                  : undefined
              }
              type="button"
            >
              {tab.label ?? tab.name}
            </button>
          );
        })}
      </div>
      <button
        aria-hidden={!showStart}
        aria-label="Previous tabs"
        className={`${fadeButtonClassName} left-0 justify-start bg-gradient-to-r from-white from-40% to-transparent pl-0.5 ${
          showStart ? '' : 'invisible pointer-events-none'
        }`}
        onClick={() => scrollByTab(-1)}
        tabIndex={showStart ? 0 : -1}
        type="button"
      >
        <FaChevronLeft size={10} />
      </button>
      <button
        aria-hidden={!showEnd}
        aria-label="More tabs"
        className={`${fadeButtonClassName} right-0 justify-end bg-gradient-to-l from-white from-40% to-transparent pr-0.5 ${
          showEnd ? '' : 'invisible pointer-events-none'
        }`}
        onClick={() => scrollByTab(1)}
        tabIndex={showEnd ? 0 : -1}
        type="button"
      >
        <FaChevronRight size={10} />
      </button>
    </div>
  );
};
